import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bot, Send, Sparkles, Mail, Loader2, Copy, Check, UserPlus, Building2, Calendar, FileText, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIAssistant = ({ currentLead = null, onLeadCreated = null, onRefresh = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your AI assistant for Axe Capital Lending. I can help you:\n\n• **Add leads** - Paste lead info in any format\n• **Add lenders** - Add new lenders to your database\n• **Find lenders** - Search lenders by criteria\n• **Recommend lenders** - Match leads with best lenders\n• **Send emails** - Draft and send emails\n• **Schedule follow-ups** - Create reminders\n• **Create term sheets** - Generate term sheets\n\nHow can I help you today?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [actionData, setActionData] = useState({});
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickActions = currentLead ? [
    { icon: <Zap className="h-3 w-3" />, label: 'Recommend Lenders', action: 'recommend' },
    { icon: <Mail className="h-3 w-3" />, label: 'Draft Email', action: 'email' },
    { icon: <Calendar className="h-3 w-3" />, label: 'Schedule Follow-up', action: 'followup' },
    { icon: <FileText className="h-3 w-3" />, label: 'Create Term Sheet', action: 'termsheet' },
  ] : [
    { icon: <UserPlus className="h-3 w-3" />, label: 'Add Lead', action: 'add_lead' },
    { icon: <Building2 className="h-3 w-3" />, label: 'Add Lender', action: 'add_lender' },
    { icon: <Zap className="h-3 w-3" />, label: 'Find Lender', action: 'search_lender' },
  ];

  const handleSend = async (customMessage = null) => {
    const message = customMessage || inputValue;
    if (!message.trim()) return;

    const userMessage = { role: 'user', content: message };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/assistant`, {
        message: message,
        lead_id: currentLead?.lead_id || null
      }, { withCredentials: true });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      toast.error('Failed to get AI response');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (action) => {
    if (action === 'add_lead' || action === 'add_lender' || action === 'search_lender') {
      setActiveAction(action);
      if (action === 'search_lender') {
        setActionData({ query: '' });
      } else {
        setActionData({ rawData: '' });
      }
      return;
    }

    if (!currentLead) {
      toast.error('Please select a lead first');
      return;
    }

    if (action === 'recommend') {
      setLoading(true);
      setMessages(prev => [...prev, { role: 'user', content: '🔍 Find best lenders for this lead...' }]);
      try {
        const response = await axios.post(`${API}/ai/recommend-lenders`, {
          message: 'Recommend lenders',
          lead_id: currentLead.lead_id
        }, { withCredentials: true });
        setMessages(prev => [...prev, { role: 'assistant', content: response.data.recommendations }]);
      } catch (error) {
        toast.error('Failed to get recommendations');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (action === 'email') {
      setActiveAction('email');
      setActionData({ subject: '', message: '' });
      return;
    }

    if (action === 'search_lender') {
      setActiveAction('search_lender');
      setActionData({ query: '' });
      return;
    }

    if (action === 'followup') {
      setActiveAction('followup');
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setActionData({ 
        type: 'CALL', 
        date: tomorrow.toISOString().split('T')[0],
        notes: '' 
      });
      return;
    }

    if (action === 'termsheet') {
      setActiveAction('termsheet');
      setActionData({
        lender_name: '',
        program_name: '',
        loan_amount: '',
        interest_rate: '',
        term_months: '12',
        ltv: ''
      });
      return;
    }
  };

  const handleActionSubmit = async () => {
    setLoading(true);
    try {
      if (activeAction === 'add_lead') {
        const response = await axios.post(`${API}/ai/create-lead`, {
          lead_data: actionData.rawData
        }, { withCredentials: true });
        
        if (response.data.success) {
          const lead = response.data.lead;
          toast.success(`Lead "${lead.first_name} ${lead.last_name}" created!`);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ **Lead Created!**\n\n**${lead.first_name} ${lead.last_name}**\n📧 ${lead.email}\n📱 ${lead.phone}\n💼 ${lead.reason_for_capital}\n💰 ${lead.amount_needed}\n🏠 ${lead.property_type}`
          }]);
          if (onRefresh) onRefresh();
        }
      } else if (activeAction === 'add_lender') {
        const response = await axios.post(`${API}/ai/create-lender`, {
          lead_data: actionData.rawData
        }, { withCredentials: true });
        
        if (response.data.success) {
          toast.success(response.data.message);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ **Lender Added!**\n\n${JSON.stringify(response.data.lender, null, 2)}`
          }]);
          if (onRefresh) onRefresh();
        }
      } else if (activeAction === 'email') {
        const response = await axios.post(`${API}/ai/send-email`, {
          action: 'send_email',
          lead_id: currentLead.lead_id,
          data: actionData
        }, { withCredentials: true });
        
        if (response.data.success) {
          toast.success('Email sent!');
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ **Email Sent!**\n\nTo: ${currentLead.email}\nSubject: ${actionData.subject}\n\n${response.data.email_sent ? '📨 Delivered' : '📝 Logged'}`
          }]);
          if (onRefresh) onRefresh();
        }
      } else if (activeAction === 'search_lender') {
        const response = await axios.post(`${API}/ai/search-lenders`, {
          message: actionData.query
        }, { withCredentials: true });
        
        if (response.data.success) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `🔍 **Lender Search Results**\n\nQuery: "${actionData.query}"\n\n${response.data.results}`
          }]);
        }
      } else if (activeAction === 'followup') {
        const response = await axios.post(`${API}/ai/create-followup`, {
          action: 'create_followup',
          lead_id: currentLead.lead_id,
          data: actionData
        }, { withCredentials: true });
        
        if (response.data.success) {
          toast.success('Follow-up scheduled!');
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ **Follow-up Scheduled!**\n\n📅 ${actionData.type} on ${actionData.date}\n📝 ${actionData.notes || 'No notes'}`
          }]);
          if (onRefresh) onRefresh();
        }
      } else if (activeAction === 'termsheet') {
        const response = await axios.post(`${API}/ai/create-termsheet`, {
          action: 'create_termsheet',
          lead_id: currentLead.lead_id,
          data: actionData
        }, { withCredentials: true });
        
        if (response.data.success) {
          toast.success('Term sheet created!');
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ **Term Sheet Created!**\n\n🏦 ${actionData.lender_name}\n📋 ${actionData.program_name}\n💰 $${parseInt(actionData.loan_amount).toLocaleString()}\n📊 ${actionData.interest_rate} rate\n⏱️ ${actionData.term_months} months`
          }]);
          if (onRefresh) onRefresh();
        }
      }
      
      setActiveAction(null);
      setActionData({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const renderActionForm = () => {
    if (activeAction === 'add_lead' || activeAction === 'add_lender') {
      return (
        <div className="space-y-3 p-3 bg-muted rounded-lg">
          <Label>{activeAction === 'add_lead' ? 'Paste Lead Information' : 'Paste Lender Information'}</Label>
          <Textarea
            value={actionData.rawData || ''}
            onChange={(e) => setActionData({ rawData: e.target.value })}
            placeholder={activeAction === 'add_lead' 
              ? "Paste lead data: Name, email, phone, loan type, amount, property, FICO, etc."
              : "Paste lender data: Name, category, contact info, loan range, FICO requirements, etc."
            }
            rows={4}
          />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setActiveAction(null)}>Cancel</Button>
            <Button size="sm" onClick={handleActionSubmit} disabled={loading || !actionData.rawData}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        </div>
      );
    }

    if (activeAction === 'email') {
      return (
        <div className="space-y-3 p-3 bg-muted rounded-lg">
          <Label>Send Email to {currentLead?.first_name}</Label>
          <Input
            placeholder="Subject"
            value={actionData.subject || ''}
            onChange={(e) => setActionData({...actionData, subject: e.target.value})}
          />
          <Textarea
            placeholder="Message..."
            value={actionData.message || ''}
            onChange={(e) => setActionData({...actionData, message: e.target.value})}
            rows={4}
          />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setActiveAction(null)}>Cancel</Button>
            <Button size="sm" onClick={handleActionSubmit} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Email'}
            </Button>
          </div>
        </div>
      );
    }

    if (activeAction === 'followup') {
      return (
        <div className="space-y-3 p-3 bg-muted rounded-lg">
          <Label>Schedule Follow-up for {currentLead?.first_name}</Label>
          <div className="grid grid-cols-2 gap-2">
            <Select value={actionData.type} onValueChange={(v) => setActionData({...actionData, type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CALL">Call</SelectItem>
                <SelectItem value="EMAIL">Email</SelectItem>
                <SelectItem value="MEETING">Meeting</SelectItem>
                <SelectItem value="SMS">SMS</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={actionData.date || ''}
              onChange={(e) => setActionData({...actionData, date: e.target.value})}
            />
          </div>
          <Input
            placeholder="Notes (optional)"
            value={actionData.notes || ''}
            onChange={(e) => setActionData({...actionData, notes: e.target.value})}
          />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setActiveAction(null)}>Cancel</Button>
            <Button size="sm" onClick={handleActionSubmit} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Schedule'}
            </Button>
          </div>
        </div>
      );
    }

    if (activeAction === 'search_lender') {
      return (
        <div className="space-y-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
          <Label className="text-blue-800 dark:text-blue-300">🔍 Search Lenders</Label>
          <Textarea
            placeholder="Describe what you're looking for...&#10;&#10;Examples:&#10;• Find me a lender for a $500k fix and flip in Nevada&#10;• Who offers DSCR loans with 680 FICO?&#10;• Lenders that do ground-up construction in California"
            value={actionData.query || ''}
            onChange={(e) => setActionData({...actionData, query: e.target.value})}
            rows={4}
          />
          <p className="text-xs text-blue-600 dark:text-blue-400">
            AI will search through all lender guidelines and programs to find matches
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setActiveAction(null)}>Cancel</Button>
            <Button size="sm" onClick={handleActionSubmit} disabled={loading || !actionData.query?.trim()}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </div>
        </div>
      );
    }

    if (activeAction === 'termsheet') {
      return (
        <div className="space-y-3 p-3 bg-muted rounded-lg">
          <Label>Create Term Sheet for {currentLead?.first_name}</Label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Lender Name"
              value={actionData.lender_name || ''}
              onChange={(e) => setActionData({...actionData, lender_name: e.target.value})}
            />
            <Input
              placeholder="Program Name"
              value={actionData.program_name || ''}
              onChange={(e) => setActionData({...actionData, program_name: e.target.value})}
            />
            <Input
              placeholder="Loan Amount"
              type="number"
              value={actionData.loan_amount || ''}
              onChange={(e) => setActionData({...actionData, loan_amount: e.target.value})}
            />
            <Input
              placeholder="Interest Rate (e.g., 10.5%)"
              value={actionData.interest_rate || ''}
              onChange={(e) => setActionData({...actionData, interest_rate: e.target.value})}
            />
            <Input
              placeholder="Term (months)"
              type="number"
              value={actionData.term_months || ''}
              onChange={(e) => setActionData({...actionData, term_months: e.target.value})}
            />
            <Input
              placeholder="LTV %"
              type="number"
              value={actionData.ltv || ''}
              onChange={(e) => setActionData({...actionData, ltv: e.target.value})}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setActiveAction(null)}>Cancel</Button>
            <Button size="sm" onClick={handleActionSubmit} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Term Sheet'}
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {/* Floating Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50 bg-primary hover:bg-primary/90"
        size="icon"
      >
        <Bot className="h-6 w-6" />
      </Button>

      {/* Chat Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[500px] h-[650px] flex flex-col p-0">
          <DialogHeader className="px-4 py-3 border-b bg-primary text-primary-foreground rounded-t-lg">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-white/20 rounded-full">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-white text-base">AI Assistant</DialogTitle>
                <DialogDescription className="text-white/70 text-xs">
                  {currentLead ? `Working on: ${currentLead.first_name} ${currentLead.last_name}` : 'Your personal assistant'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Action Form */}
          {activeAction && (
            <div className="px-4 pb-2">
              {renderActionForm()}
            </div>
          )}

          {/* Quick Actions */}
          {!activeAction && (
            <div className="px-4 pb-2">
              <div className="flex flex-wrap gap-1">
                {quickActions.map((qa, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 gap-1"
                    onClick={() => handleQuickAction(qa.action)}
                    disabled={loading}
                  >
                    {qa.icon}
                    {qa.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t">
            <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask me anything..."
                disabled={loading}
                className="flex-1"
              />
              <Button type="submit" size="icon" disabled={loading || !inputValue.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AIAssistant;
